import { useEffect, useState, type FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  uiStyles,
  UiSpinner,
} from '@/lib/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { type ImportAttemptsTableRow, loadImportAttemptsForTable, loadImportAttemptsForTableQueryKey } from '@/api/importer';
import { type ApiResponse } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import DataTable from './DataTable';
import BaseLoadingSpinner from '@/base/Loading/Spinner';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useLocalStorage } from '@uidotdev/usehooks';
import { type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState } from 'mantine-react-table';

dayjs.extend(utc);
interface Params {
  filters: any
  sorting: any
  pagination: any
  search: string
  modelType: string
}

const useGetImportAttemptsForTable = ({ filters, sorting, pagination, search, modelType }: Params & { setPagination: React.Dispatch<React.SetStateAction<any>> }) => {
  const { createTenantAdminApiRequest, isLoading: isTenantLoading } = useTenantApi();
  return useQuery<ApiResponse<ImportAttemptsTableRow>, Error>({
    queryKey: [loadImportAttemptsForTableQueryKey, pagination, filters, sorting, search],
    queryFn: async (): Promise<ApiResponse<ImportAttemptsTableRow>> => {
      return await loadImportAttemptsForTable(createTenantAdminApiRequest)({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        modelType,
        filters,
        sorting,
        search
      });
    },
    keepPreviousData: true,
    refetchInterval: 5_000,
    enabled: !isTenantLoading,
  });
};
export interface ImportTableProps {
  modelType: string
}

const LOCAL_TABLE_STORAGE_KEY = 'importer_registration_table';

const ImportTable: FC<ImportTableProps> = ({ modelType }) => {
  const { tenantCode } = useRegisterRoute();
  const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>(`${LOCAL_TABLE_STORAGE_KEY}_filter_${tenantCode}`, []);
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(`${LOCAL_TABLE_STORAGE_KEY}_sorting_${tenantCode}`, []);
  const [search, setSearch] = useLocalStorage<string>(`${LOCAL_TABLE_STORAGE_KEY}_search_${tenantCode}`, '');
  const [pagination, setPagination] = useLocalStorage<MRT_PaginationState>(`${LOCAL_TABLE_STORAGE_KEY}_pagination_${tenantCode}`, { pageIndex: 0, pageSize: 10 });
  const { reportToGlobal } = useApiErrorHandler();
  const { data, isLoading, isError, error, isFetching, refetch } = useGetImportAttemptsForTable({ filters, sorting, pagination, search, setPagination, modelType });
  useEffect(
    () => {
      if (error) {
        reportToGlobal(error);
      }
    },
    [error]
  );
  if (!data || isLoading) {
    return (
      <BaseLoadingSpinner />
    );
  }

  if (data.items && Array.isArray(data.items)) {
    return (
      <DataTable
        data={data.items}
        isLoading={isLoading}
        isError={isError}
        isFetching={isFetching}
        refetch={refetch}
        filters={filters}
        setFilters={setFilters}
        sorting={sorting}
        setSorting={setSorting}
        search={search}
        setSearch={setSearch}
        pagination={pagination}
        setPagination={setPagination}
        statusSelectOptions={data.selectOptions?.statusSelectOptions ?? []}
        rowCount={data.pagination?.total ?? 0}
        modelType={modelType}
      />
    );
  } else {
    return (
      <BaseMessageBarInfo borderRadius={uiStyles.borderRadius}>
        No registration import attempts
      </BaseMessageBarInfo>
    );
  }
};

export default ImportTable;
